import React, { createContext, useState, useContext, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import PropTypes from 'prop-types'; // Importar PropTypes

// Crear el contexto
const NotificationContext = createContext(null);

// Crear el Provider
export const NotificationProvider = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // 'success', 'error', 'warning', 'info'

  // Función para mostrar la notificación (memoizada)
  const showNotification = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Función para cerrar el Snackbar
  const handleCloseSnackbar = (event, reason) => {
    // Evitar cerrar al hacer clic fuera
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Valor proporcionado por el contexto
  const contextValue = {
    showNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // Duración en ms (6 segundos)
        onClose={handleCloseSnackbar}
        // Anclar a la esquina inferior izquierda (o donde prefieras)
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {/* Usar Alert dentro de Snackbar para colores y estilos */}
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled" // Estilo relleno
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

// Hook personalizado para usar el contexto fácilmente
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

// Definir PropTypes para el Provider
NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};